import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  useTheme,
  InputBase,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
  Fade,
} from '@mui/material';
import { MdEdit } from 'react-icons/md';
import SpaRoundedIcon from '@mui/icons-material/SpaRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ImageNotSupportedRoundedIcon from '@mui/icons-material/ImageNotSupportedRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DashboardCard2 from 'src/components/shared/DashboardCard2';
import { useMain } from '../../hooks/useMain';

// Default gallery images the user can pick a category image from, instead of
// (or in addition to) uploading their own file.
const DEFAULT_GALLERY_IMAGES = [
  'https://images.pexels.com/photos/3993324/pexels-photo-3993324.jpeg?auto=compress&cs=tinysrgb&h=750&w=1000',
  'https://res.cloudinary.com/zwsnisd5/image/upload/v1789042504/WhatsApp_Image_2026-09-10_at_5.40.25_PM.jpg',
  'https://res.cloudinary.com/zwsnisd5/image/upload/v1789042504/WhatsApp_Image_2026-09-10_at_5.40.07_PM.jpg',
  'https://res.cloudinary.com/zwsnisd5/image/upload/v1789042505/WhatsApp_Image_2026-09-10_at_5.39.56_PM.jpg',
  'https://res.cloudinary.com/zwsnisd5/image/upload/v1789042504/WhatsApp_Image_2026-09-10_at_5.39.35_PM.jpg',
  'https://res.cloudinary.com/zwsnisd5/image/upload/v1789042504/WhatsApp_Image_2026-09-10_at_5.39.21_PM.jpg',
  'https://res.cloudinary.com/zwsnisd5/image/upload/v1789042504/WhatsApp_Image_2026-09-10_at_5.40.17_PM.jpg',
  'https://res.cloudinary.com/zwsnisd5/image/upload/v1789044729/WhatsApp_Image_2026-09-10_at_5.42.09_PM.jpg',
  'https://res.cloudinary.com/zwsnisd5/image/upload/v1789044729/WhatsApp_Image_2026-09-10_at_5.41.42_PM.jpg',
  'https://res.cloudinary.com/zwsnisd5/image/upload/v1789044729/WhatsApp_Image_2026-09-10_at_5.41.52_PM.jpg',
  'https://res.cloudinary.com/zwsnisd5/image/upload/v1789044530/WhatsApp_Image_2026-09-10_at_5.41.18_PM.jpg',
  'https://res.cloudinary.com/zwsnisd5/image/upload/v1789044531/WhatsApp_Image_2026-09-10_at_5.40.46_PM.jpg',
  'https://res.cloudinary.com/zwsnisd5/image/upload/v1789044531/WhatsApp_Image_2026-09-10_at_5.40.55_PM.jpg',
  'https://res.cloudinary.com/zwsnisd5/image/upload/v1789044531/WhatsApp_Image_2026-09-10_at_5.41.07_PM.jpg',
  'https://res.cloudinary.com/zwsnisd5/image/upload/v1789044901/WhatsApp_Image_2026-09-10_at_5.41.42_PM_1.jpg',
  'https://res.cloudinary.com/zwsnisd5/image/upload/v1789044901/WhatsApp_Image_2026-09-10_at_5.42.58_PM.jpg',
];

const toTitleCase = (str) => {
  if (typeof str !== 'string') str = String(str || '');
  return str
    .trim()
    .toLowerCase()
    .split(' ')
    .filter((w) => w.trim().length > 0)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

// Small deterministic gradient generator so categories without an image still
// look distinct and polished instead of a plain grey box.
const GRADIENTS = [
  'linear-gradient(135deg,#667eea 0%,#764ba2 100%)',
  'linear-gradient(135deg,#f093fb 0%,#f5576c 100%)',
  'linear-gradient(135deg,#4facfe 0%,#00f2fe 100%)',
  'linear-gradient(135deg,#43e97b 0%,#38f9d7 100%)',
  'linear-gradient(135deg,#fa709a 0%,#fee140 100%)',
  'linear-gradient(135deg,#30cfd0 0%,#330867 100%)',
  'linear-gradient(135deg,#a8edea 0%,#fed6e3 100%)',
  'linear-gradient(135deg,#ff9a9e 0%,#fecfef 100%)',
];
const gradientFor = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return GRADIENTS[Math.abs(hash) % GRADIENTS.length];
};

const CategoryWiseServices = ({ data, activeBranchId: activeBranchIdProp, onBack }) => {
  const {
    activeBranchId: activeBranchIdFromHook,
    getAllServiceCategories,
    serviceCreateCategory,
    serviceUpdateCategory,
    setShowMessage,
  } = useMain();
  const activeBranchId =
    activeBranchIdProp ||
    activeBranchIdFromHook ||
    localStorage.getItem('branchId');
  const theme = useTheme();

  const [search, setSearch] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null); // { categoryName, serviceCount, forSummary, existingDoc }
  const [editName, setEditName] = useState('');
  const [editImageFile, setEditImageFile] = useState(null);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
  const [imageTab, setImageTab] = useState(0); // 0 = gallery, 1 = upload
  const [saving, setSaving] = useState(false);
const [viewServicesCategory, setViewServicesCategory] = useState(null); // holds the row object to view
  const [existingCategoryDocs, setExistingCategoryDocs] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);

  useEffect(() => {
    if (activeBranchId) {
      loadExistingCategoryDocs();
    }
  }, [activeBranchId]);

  const loadExistingCategoryDocs = async () => {
    try {
      const branch = activeBranchId || localStorage.getItem('branchId');
      if (!branch) return;
      const res = await getAllServiceCategories(branch);
      if (res?.statusCode === 200 || Array.isArray(res?.data)) {
        setExistingCategoryDocs(res.data || []);
      }
    } catch (error) {
      console.error('Failed to load category docs', error);
    } finally {
      setCategoriesLoaded(true);
    }
  };

  const buildForSummary = (services = []) =>
    services.reduce((acc, s) => {
      const key = s?.serviceFor || 'Both';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

  const rows = useMemo(() => {
    const dataSource = data && Array.isArray(data) && data.length > 0 ? data : [];
    if (dataSource.length === 0) return [];

    let list = [];
    const firstItem = dataSource[0];

    const isNewShape =
      firstItem &&
      typeof firstItem === 'object' &&
      'categoryName' in firstItem &&
      Array.isArray(firstItem.services);

    const isCategorizedMap =
      !isNewShape &&
      firstItem &&
      typeof firstItem === 'object' &&
      !('serviceName' in firstItem) &&
      !('category' in firstItem);

    if (isNewShape) {
      // New API shape: [{ categoryName, image: { url, public_id } | null, services: [...] }]
      list = dataSource.map((entry) => {
        const services = Array.isArray(entry.services) ? entry.services : [];
        return {
          categoryName: entry.categoryName,
          services,
          serviceCount: services.length,
          image: entry.image || null,
          forSummary: buildForSummary(services),
        };
      });
    } else if (isCategorizedMap) {
      list = dataSource.map((entry) => {
        const categoryName = Object.keys(entry)[0];
        const services = entry[categoryName] || [];
        return {
          categoryName,
          services,
          serviceCount: services.length,
          image: null,
          forSummary: buildForSummary(services),
        };
      });
    } else {
      const groups = {};
      dataSource.forEach((s) => {
        const cat = s?.category || 'Uncategorized';
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(s);
      });
      list = Object.keys(groups).map((categoryName) => {
        const services = groups[categoryName];
        return {
          categoryName,
          services,
          serviceCount: services.length,
          image: null,
          forSummary: buildForSummary(services),
        };
      });
    }

    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter((r) => r.categoryName?.toLowerCase().includes(q));
  }, [data, search]);

  const findExistingDoc = (categoryName) => {
    const titleCased = toTitleCase(categoryName);
    return existingCategoryDocs.find((c) => c.categoryName === titleCased) || null;
  };

  const handleUpdateCategory = (row) => {
    const existingDoc = findExistingDoc(row.categoryName);
    setSelectedCategory({ ...row, existingDoc });
    setEditName(existingDoc ? existingDoc.categoryName : row.categoryName);
    setEditImageFile(null);
    setSelectedGalleryImage(null);
    setImageTab(0);
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    if (saving) return;
    setIsPopupOpen(false);
    setSelectedCategory(null);
    setEditName('');
    setEditImageFile(null);
    setSelectedGalleryImage(null);
  };

  const handleSaveCategory = async () => {
    if (!selectedCategory) return;
    if (!editName.trim()) {
      setShowMessage({ message: 'Category name is required', messageType: 'error' });
      return;
    }
    const branch = activeBranchId || localStorage.getItem('branchId');
    if (!branch) {
      setShowMessage({ message: 'Branch details are required', messageType: 'error' });
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('categoryName', editName.trim());
      formData.append('name', editName.trim());
      formData.append('category', editName.trim());
      formData.append('branchDetails', branch);
      formData.append('branchId', branch);

            if (editImageFile) {
        // A locally uploaded file takes priority
        formData.append('image', editImageFile);
      } else if (selectedGalleryImage) {
        // Convert the picked gallery image URL into an actual file, so it's
        // sent under the SAME 'image' field the backend expects — identical
        // to a manual upload.
        try {
          const imgResponse = await fetch(selectedGalleryImage);
          const imgBlob = await imgResponse.blob();
          const fileName = selectedGalleryImage.split('/').pop().split('?')[0] || 'category-image.jpg';
          const galleryFile = new File([imgBlob], fileName, { type: imgBlob.type || 'image/jpeg' });
          formData.append('image', galleryFile);
        } catch (err) {
          console.error('Failed to convert gallery image to file', err);
          setShowMessage({ message: 'Failed to attach selected image, please try uploading manually', messageType: 'error' });
          setSaving(false);
          return;
        }
      }

      let res;
      if (selectedCategory.existingDoc?._id) {
        res = await serviceUpdateCategory(selectedCategory.existingDoc._id, formData);
      } else {
        res = await serviceCreateCategory(formData);
      }

      if (
        res?.statusCode === 200 ||
        res?.statusCode === 201 ||
        res?.status === 'success' ||
        res?.data?._id ||
        res?._id
      ) {
        setShowMessage({
          message: selectedCategory.existingDoc ? 'Category updated successfully' : 'Category created successfully',
          messageType: 'success',
        });
        await loadExistingCategoryDocs();
      } else {
        setShowMessage({ message: res?.message || 'Save failed', messageType: 'error' });
      }
    } catch (error) {
      setShowMessage({ message: error.message || 'Something went wrong', messageType: 'error' });
    } finally {
      setSaving(false);
      handleClosePopup();
    }
  };

  if (!data || data.length === 0) {
    return (
      <DashboardCard2 title="Categories" onBack={onBack}>
        <Box
          sx={{
            py: 10,
            textAlign: 'center',
            color: theme.palette.text.secondary,
            borderRadius: 3,
            background: 'linear-gradient(180deg, rgba(102,126,234,0.05) 0%, rgba(118,75,162,0.02) 100%)',
          }}
        >
          <SpaRoundedIcon sx={{ fontSize: 48, mb: 1.5, opacity: 0.4, color: '#764ba2' }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            No categories yet
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.7 }}>
            Categories will appear here once services are added.
          </Typography>
        </Box>
      </DashboardCard2>
    );
  }

  const currentPreview = editImageFile
    ? URL.createObjectURL(editImageFile)
    : selectedGalleryImage ||
      (selectedCategory?.existingDoc?.image?.url ?? null);

  return (
    <DashboardCard2 title="Categories" onBack={onBack}>
      {/* ---------------------------------------------------------------- */}
      {/* Header / search bar                                              */}
      {/* ---------------------------------------------------------------- */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            {rows.length} {rows.length === 1 ? 'Category' : 'Categories'}
          </Typography>
          
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: `1.5px solid ${theme.palette.primary.main}30`,
            borderRadius: '10px',
            px: 1.5,
            height: 42,
            minWidth: 260,
            background: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            transition: 'border-color 0.2s',
            '&:focus-within': {
              borderColor: theme.palette.primary.main,
            },
          }}
        >
          <SearchRoundedIcon sx={{ fontSize: 19, color: theme.palette.text.secondary, mr: 1 }} />
          <InputBase
            placeholder="Search categories..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, fontSize: '0.875rem' }}
          />
          {search && (
            <IconButton size="small" onClick={() => setSearch('')}>
              <ClearRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* ---------------------------------------------------------------- */}
      {/* Category cards grid                                              */}
      {/* ---------------------------------------------------------------- */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 2.5,
        }}
      >
        {rows.map((row) => {
          const existingDoc = findExistingDoc(row.categoryName);
          const imageUrl = row.image?.url || existingDoc?.image?.url || null;

          return (
            <Fade in key={row.categoryName}>
              <Box
                sx={{
                  borderRadius: '16px',
                  overflow: 'hidden',
                  background: '#fff',
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: '0 2px 10px rgba(17,17,26,0.06)',
                  transition: 'transform 0.22s ease, box-shadow 0.22s ease',
                  cursor: 'default',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 24px rgba(17,17,26,0.12)',
                  },
                }}
              >
            {/* Image / no-image placeholder header */}
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: 190,
                    background: '#eceef2',
                    display: 'flex',
                    alignItems: 'flex-end',
                    overflow: 'hidden',
                  }}
                >
                  {imageUrl ? (
                    <Box
                      component="img"
                      src={imageUrl}
                      alt={row.categoryName}
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.text.disabled,
                      }}
                    >
                   <ImageNotSupportedRoundedIcon sx={{ fontSize: 40, mb: 0.75, opacity: 0.6 }} />
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, opacity: 0.7 }}>
                        No image
                      </Typography>
                    </Box>
                  )}

                  {imageUrl && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.55) 100%)',
                      }}
                    />
                  )}

                  <Typography
                    sx={{
                      position: 'relative',
                      color: imageUrl ? '#fff' : theme.palette.text.primary,
                      fontWeight: 700,
                      fontSize: '1rem',
                      px: 2,
                      pb: 1.25,
                      textShadow: imageUrl ? '0 1px 4px rgba(0,0,0,0.4)' : 'none',
                    }}
                  >
                    {row.categoryName}
                  </Typography>

                  <Tooltip title="Edit category">
                    <IconButton
                      onClick={() => handleUpdateCategory(row)}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        background: 'rgba(255,255,255,0.85)',
                        backdropFilter: 'blur(4px)',
                        '&:hover': { background: '#fff' },
                        width: 30,
                        height: 30,
                      }}
                    >
                      <MdEdit style={{ color: '#4a4a4a', fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Body */}
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                    <Chip
  label={`${row.serviceCount} service${row.serviceCount !== 1 ? 's' : ''}`}
  size="small"
  sx={{
    height: 24,
    fontSize: '0.72rem',
    fontWeight: 700,
    background: '#6274DD',
    color: '#fff',
  }}
/>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                    {Object.entries(row.forSummary).map(([gender, count]) => (
                      <Chip
                        key={gender}
                        label={`${gender}: ${count}`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 21, fontSize: '0.68rem' }}
                      />
                    ))}
                               <Button
                    fullWidth
                    size="small"
                    onClick={() => setViewServicesCategory(row)}
                    sx={{
                      mt: 1.5,
                      textTransform: 'none',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      background: 'rgba(102,126,234,0.08)',
                      '&:hover': { background: 'rgba(102,126,234,0.15)' },
                    }}
                  >
                    View {row.serviceCount} Service{row.serviceCount !== 1 ? 's' : ''}
                  </Button>
                  </Box>
                </Box>
              </Box>
            </Fade>
          );
        })}

        {rows.length === 0 && (
          <Box
            sx={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              py: 8,
              color: theme.palette.text.secondary,
            }}
          >
            No categories match "{search}"
          </Box>
        )}
      </Box>

      {/* ---------------------------------------------------------------- */}
      {/* Edit / create dialog                                             */}
      {/* ---------------------------------------------------------------- */}
      <Dialog
        open={isPopupOpen}
        onClose={handleClosePopup}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            background: '#6274DD',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
              {selectedCategory?.existingDoc ? 'Update Category' : 'Set Up Category'}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', opacity: 0.85 }}>
              {selectedCategory?.categoryName}
            </Typography>
          </Box>
          <IconButton onClick={handleClosePopup} disabled={saving} sx={{ color: '#fff' }}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 1 }}>
          {/* Live preview */}
           <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: 220,
              borderRadius: '16px',
              overflow: 'hidden',
              mb: 3,
              background: '#eceef2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.4), 0 4px 16px rgba(17,17,26,0.06)',
            }}
          >
            {currentPreview ? (
              <>
                <Box
                  component="img"
                  src={currentPreview}
                  alt="Category preview"
                  sx={{
                    maxWidth: '92%',
                    maxHeight: '85%',
                    objectFit: 'contain',
                    borderRadius: '10px',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                    transition: 'transform 0.25s ease',
                    '&:hover': { transform: 'scale(1.03)' },
                  }}
                />

                {/* Top-left status badge */}
                <Chip
                  size="small"
                  icon={<CheckCircleRoundedIcon sx={{ fontSize: 16, color: '#fff !important' }} />}
                  label="Selected"
                  sx={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                    height: 24,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: '#fff',
                    background: 'rgba(76,175,80,0.9)',
                    backdropFilter: 'blur(4px)',
                  }}
                />

                {/* Remove image button */}
                <Tooltip title="Remove image">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditImageFile(null);
                      setSelectedGalleryImage(null);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      width: 28,
                      height: 28,
                      background: 'rgba(255,255,255,0.9)',
                      backdropFilter: 'blur(4px)',
                      '&:hover': { background: '#fff' },
                    }}
                  >
                    <CloseRoundedIcon sx={{ fontSize: 16, color: '#555' }} />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', color: theme.palette.text.disabled }}>
                <ImageNotSupportedRoundedIcon sx={{ fontSize: 34, mb: 0.5, opacity: 0.6 }} />
                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', opacity: 0.75 }}>
                  No image selected
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', opacity: 0.6 }}>
                  Pick from gallery or upload below
                </Typography>
              </Box>
            )}
          </Box>
          <TextField
            fullWidth
            label="Category Name"
            value={editName}
            disabled
            sx={{ mt: 1, mb: 2 }}
          />

          <Tabs
            value={imageTab}
            onChange={(_, v) => setImageTab(v)}
            variant="fullWidth"
            sx={{
              mb: 2,
              minHeight: 40,
              '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' },
            }}
          >
            <Tab icon={<PhotoLibraryRoundedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Choose from Gallery" />
            <Tab icon={<CloudUploadRoundedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Upload Your Own" />
          </Tabs>

          {imageTab === 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(3, 1fr)',
                  md: 'repeat(4, 1fr)',
                },
                gap: 1.75,
                maxHeight: 460,
                overflowY: 'auto',
                p: 0.5,
              }}
            >
              {DEFAULT_GALLERY_IMAGES.map((url) => {
                const isSelected = selectedGalleryImage === url;
                return (
                  <Box
                    key={url}
                    onClick={() => {
                      setSelectedGalleryImage(url);
                      setEditImageFile(null);
                    }}
                    sx={{
                      position: 'relative',
                      height: 150,
                      borderRadius: '12px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      background: `url(${url})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      boxShadow: isSelected
                        ? `0 0 0 3px ${theme.palette.primary.main}`
                        : '0 2px 6px rgba(0,0,0,0.12)',
                      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                      '&:hover': { transform: 'scale(1.04)' },
                    }}
                  >
                    {isSelected && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          background: 'rgba(0,0,0,0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CheckCircleRoundedIcon sx={{ color: '#fff', fontSize: 32 }} />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {imageTab === 1 && (
            <Box
              sx={{
                border: `2px dashed ${theme.palette.primary.main}50`,
                borderRadius: '12px',
                p: 3,
                textAlign: 'center',
                background: 'rgba(102,126,234,0.04)',
              }}
            >
              <CloudUploadRoundedIcon sx={{ fontSize: 34, color: theme.palette.primary.main, mb: 1 }} />
              <Typography variant="body2" sx={{ mb: 1.5, color: theme.palette.text.secondary }}>
                Upload a custom image (PNG, JPG, JPEG)
              </Typography>
              <Button variant="outlined" component="label" size="small">
                Choose File
                <input
                  type="file"
                  hidden
                  accept="image/png, image/jpg, image/jpeg"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setEditImageFile(file);
                    if (file) setSelectedGalleryImage(null);
                  }}
                />
              </Button>
              {editImageFile && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: theme.palette.text.secondary }}>
                  Selected: {editImageFile.name}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleClosePopup} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveCategory}
            variant="contained"
            disabled={saving}
            sx={{
              background: '#6274DD',
              px: 3,
              '&:hover': { background: 'linear-gradient(135deg,#5a6fd8 0%,#6a4293 100%)' },
            }}
          >
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : selectedCategory?.existingDoc ? 'Save Changes' : 'Create Category'}
          </Button>
        </DialogActions>
      </Dialog>
            {/* ---------------------------------------------------------------- */}
      {/* View services dialog — keeps cards a fixed size                  */}
      {/* ---------------------------------------------------------------- */}
      <Dialog
        open={Boolean(viewServicesCategory)}
        onClose={() => setViewServicesCategory(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            background: '#6274DD',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
              {viewServicesCategory?.categoryName}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', opacity: 0.85 }}>
              {viewServicesCategory?.serviceCount} service{viewServicesCategory?.serviceCount !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <IconButton onClick={() => setViewServicesCategory(null)} sx={{ color: '#fff' }}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
            {(viewServicesCategory?.services || []).map((s) => (
              <Box
                key={s._id || s.serviceName}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: '10px',
                  background: 'rgba(102,126,234,0.06)',
                  border: `1px solid ${theme.palette.divider}`,
                  '&:hover': { background: 'rgba(102,126,234,0.12)' },
                }}
              >
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: theme.palette.text.primary }}>
                  {s.serviceName}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'center', flexShrink: 0 }}>
                  {s.duration ? (
                    <Chip
                      label={`${s.duration}m`}
                      size="small"
                      sx={{ height: 20, fontSize: '0.68rem', background: '#fff', border: `1px solid ${theme.palette.divider}` }}
                    />
                  ) : null}
                  <Chip
                      label={s.serviceFor || 'Both'}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        color: '#fff',
                        background: '#6274DD',
                      }}
                    />
                </Box>
              </Box>
            ))}
            {(!viewServicesCategory?.services || viewServicesCategory.services.length === 0) && (
              <Typography sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary, textAlign: 'center', py: 3 }}>
                No services in this category
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </DashboardCard2>
  );
};

export default CategoryWiseServices;